import { atom, type getDefaultStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { getSafeLocalStorage } from './safe-storage';

/**
 * localStorage key caching the current user's `user_ref_id` — written when the
 * user resolves (see `useCacheUserRefIdForStorage`) and cleared on logout. A
 * plain synchronous cache of the id, kept so per-user storage keys resolve at
 * `atomWithStorage`'s one-shot `getOnInit` read even on a cold refresh, where
 * the `GET_USER` query cache restores too late for that first read.
 */
export const CURRENT_USER_REF_ID_STORAGE_KEY = 'heidi.currentUserRefId';

/**
 * Current user's id for namespacing per-user localStorage, read synchronously
 * from `CURRENT_USER_REF_ID_STORAGE_KEY`. `null` when absent (logged out, or a
 * first login before it's been written) — reads/writes then no-op rather than
 * falling back to a shared, unscoped bucket.
 */
export const getUserStorageScope = (): string | null =>
  getSafeLocalStorage().getItem(CURRENT_USER_REF_ID_STORAGE_KEY);

const scopedKey = (key: string, scope: string) => `${key}_${scope}`;

export const createUserScopedStorageAdapter = (base = getSafeLocalStorage()) => ({
  getItem: (key: string) => {
    const scope = getUserStorageScope();
    return scope ? base.getItem(scopedKey(key, scope)) : null;
  },
  setItem: (key: string, value: string) => {
    const scope = getUserStorageScope();
    if (scope) {
      base.setItem(scopedKey(key, scope), value);
    }
  },
  removeItem: (key: string) => {
    const scope = getUserStorageScope();
    if (scope) {
      base.removeItem(scopedKey(key, scope));
    }
  },
});

export const createUserScopedJotaiStorage = <T>() =>
  createJSONStorage<T>(() => createUserScopedStorageAdapter());

type EnumerableStringStorage = {
  keys: () => string[];
  removeItem: (key: string) => void;
};

const noopEnumerableStorage: EnumerableStringStorage = {
  keys: () => [],
  removeItem: () => undefined,
};

/** `getSafeLocalStorage` exposes only get/set/remove, so a sweep over existing
 *  entries needs its own guarded enumeration. */
const getEnumerableLocalStorage = (): EnumerableStringStorage => {
  if (typeof window === 'undefined') {
    return noopEnumerableStorage;
  }
  try {
    const storage = window.localStorage;
    return {
      keys: () => {
        try {
          return Object.keys(storage);
        } catch {
          return [];
        }
      },
      removeItem: (key: string) => {
        try {
          storage.removeItem(key);
        } catch {
          // Ignore — restricted webviews may block storage access.
        }
      },
    };
  } catch {
    return noopEnumerableStorage;
  }
};

/**
 * Removes every entry this adapter wrote for `scope`, except the base keys in
 * `keep`. Matches on the `_<scope>` suffix rather than an enumerated key list,
 * so a new `createUserScopedStorageAtom` is swept without a change here. Must
 * run while `CURRENT_USER_REF_ID_STORAGE_KEY` still resolves the scope.
 */
export const clearUserScopedKeysForScope = (
  scope: string,
  keep: readonly string[] = [],
  base: EnumerableStringStorage = getEnumerableLocalStorage()
): void => {
  const suffix = scopedKey('', scope);
  const kept = new Set(keep.map((key) => scopedKey(key, scope)));
  try {
    base
      .keys()
      .filter(
        (key) => key.length > suffix.length && key.endsWith(suffix) && !kept.has(key)
      )
      .forEach((key) => base.removeItem(key));
  } catch {
    // Ignore — this runs first at logout, so a storage failure here must not stop
    // the caller from clearing the rest.
  }
};

type JotaiStore = ReturnType<typeof getDefaultStore>;
const rehydrators = new Set<(store: JotaiStore) => void>();

/** Bumped by `rehydrateUserScopedAtoms`; watched by consumers whose runtime
 *  state is derived from a user-scoped atom rather than read from it directly. */
export const userScopedStorageRehydratedAtom = atom(0);

/**
 * A user-scoped, persisted `atomWithStorage` that registers itself for
 * rehydration. `getOnInit` gives an instant restore on a cold refresh, but that
 * first read falls back to the default when the user id isn't cached yet (first
 * login, or re-login after logout cleared it) — the id then resolves async
 * (`useCacheUserRefIdForStorage`) and `rehydrateUserScopedAtoms` re-reads.
 */
export const createUserScopedStorageAtom = <T>(key: string, initialValue: T) => {
  const storage = createUserScopedJotaiStorage<T>();
  const storageAtom = atomWithStorage<T>(key, initialValue, storage, { getOnInit: true });
  rehydrators.add((store) => {
    store.set(storageAtom, storage.getItem(key, initialValue));
  });
  return storageAtom;
};

/**
 * Re-reads every atom created via `createUserScopedStorageAtom` (global, not
 * scoped to a caller) and bumps `userScopedStorageRehydratedAtom`. Must run only
 * after the resolved user id has been written to `CURRENT_USER_REF_ID_STORAGE_KEY`
 * — otherwise the re-reads resolve the wrong (or no) scope.
 */
export const rehydrateUserScopedAtoms = (store: JotaiStore): void => {
  rehydrators.forEach((rehydrate) => rehydrate(store));
  store.set(userScopedStorageRehydratedAtom, (n) => n + 1);
};
